import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ListBrowseToolbar,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useDebouncedValue,
} from '@yeshuman/sdk'

import {
  useMarketplaceInterests,
  type MarketplaceInterestPeriod,
} from '../../hooks/use-marketplace-interests'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

export function MarketplaceInterestPage() {
  const [period, setPeriod] = useState<MarketplaceInterestPeriod>('7d')
  const [provider, setProvider] = useState('')
  const [hasNote, setHasNote] = useState<'any' | 'yes' | 'no'>('any')
  const [searchDraft, setSearchDraft] = useState('')
  const [offset, setOffset] = useState(0)
  const q = useDebouncedValue(searchDraft.trim(), SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    setOffset(0)
  }, [q])

  const filters = useMemo(
    () => ({
      period,
      provider,
      q,
      hasNote,
      offset,
      limit: PAGE_SIZE,
    }),
    [period, provider, q, hasNote, offset],
  )

  const { data, isLoading, error } = useMarketplaceInterests(filters)
  const status = (error as (Error & { status?: number }) | null)?.status

  if (status === 401 || status === 403) {
    return (
      <PageContainer className="max-w-5xl">
        <PageHeader
          leading={<PageHeaderLeading />}
          title="Marketplace interest"
          description="Platform operators only."
        />
        <p className="text-sm text-muted-foreground">
          Switch to the platform focus to browse candidate interest notes.
        </p>
      </PageContainer>
    )
  }

  const total = data?.total ?? 0
  const canPrev = offset > 0
  const canNext = offset + PAGE_SIZE < total

  return (
    <PageContainer className="max-w-5xl space-y-6">
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title="Marketplace interest"
          description="Filterable list of candidate interest on marketplace listings. Full notes are visible to platform operators only — not employers."
        />
        <div className="flex flex-col gap-3">
          <ListBrowseToolbar
            searchId="marketplace-interest-search"
            searchPlaceholder="Search candidate, job, board, or note"
            searchValue={searchDraft}
            onSearchChange={setSearchDraft}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={period}
              onValueChange={(value) => {
                setOffset(0)
                setPeriod(value as MarketplaceInterestPeriod)
              }}
            >
              <SelectTrigger className="w-[140px]" aria-label="Period">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={provider || 'all'}
              onValueChange={(value) => {
                setOffset(0)
                setProvider(value === 'all' ? '' : value)
              }}
            >
              <SelectTrigger className="w-[160px]" aria-label="Board">
                <SelectValue placeholder="Board" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All boards</SelectItem>
                {(data?.providers ?? []).map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={hasNote}
              onValueChange={(value) => {
                setOffset(0)
                setHasNote(value as 'any' | 'yes' | 'no')
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Note filter">
                <SelectValue placeholder="Notes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any note</SelectItem>
                <SelectItem value="yes">Has note</SelectItem>
                <SelectItem value="no">No note</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading…' : `${total} interest${total === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
      </PageHeaderStack>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      {!error && !isLoading && total === 0 ? (
        <p className="text-sm text-muted-foreground">No interests match these filters.</p>
      ) : null}

      {data && data.results.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Board</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.results.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="align-top">
                    <div className="font-medium">{row.candidate_name}</div>
                    {row.candidate_email ? (
                      <div className="text-xs text-muted-foreground">{row.candidate_email}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    <Link
                      to={row.job_path}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {row.job_title}
                    </Link>
                  </TableCell>
                  <TableCell className="align-top capitalize">{row.provider}</TableCell>
                  <TableCell className="align-top tabular-nums text-muted-foreground">
                    {new Date(row.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="align-top max-w-md whitespace-pre-wrap text-sm">
                    {row.note.trim() || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {total > PAGE_SIZE ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md bg-muted px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            disabled={!canPrev || isLoading}
            onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
          >
            Previous
          </button>
          <button
            type="button"
            className="rounded-md bg-muted px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            disabled={!canNext || isLoading}
            onClick={() => setOffset((value) => value + PAGE_SIZE)}
          >
            Next
          </button>
          <span className="text-sm text-muted-foreground">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
        </div>
      ) : null}
    </PageContainer>
  )
}
